import React, { useEffect, useRef, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import CardView from '../../components/Redesing/card-view';
import IconButtonMenu from '../../components/Redesing/icon-button-menu';
import SaveIcon from '@mui/icons-material/Save';
import { Stack } from '@mui/system';
import '../../css/TOS.scss';
import { Skeleton, useTheme } from '@mui/material';

const EDITOR_HEIGHT = 1000;

interface TermsProps {
  isLoaded: boolean;
  getSystemTOS: () => Promise<string>;
  submit: (tos: { text: string; original: string }) => void;
  isChanged: (flag: boolean) => void;
  buttonDisabled: boolean;
}

const Terms: React.FC<TermsProps> = ({
  isLoaded,
  getSystemTOS,
  submit,
  buttonDisabled,
  isChanged,
}) => {
  const [original, setOriginal] = useState<string>('');
  const [text, setText] = useState<string>('');
  const [loaded, setLoaded] = useState<boolean>(false);
  const editorRef = useRef<any>(null);
  const theme = useTheme();

  useEffect(() => {
    const fetchData = async () => {
      if (isLoaded) {
        const response = await getSystemTOS();
        setText(response || '');
        setOriginal(response || '');
        setLoaded(true);
      }
    };

    fetchData();
  }, [isLoaded, getSystemTOS]);

  const handleEditorInit = (evt: any, editor: any) => {
    editorRef.current = editor;
  };

  const handleEditorChange = () => {
    const newTos = {
      text: editorRef.current.getContent(),
      original: original,
    };
    submit(newTos);
  };

  const renderEditor = () => {
    return (
      <Editor
        tinymceScriptSrc={`${process.env.PUBLIC_URL}/tinymce/tinymce.min.js`}
        onInit={handleEditorInit}
        initialValue={text}
        init={{
          skin: theme.palette.mode === 'dark' ? 'oxide-dark' : undefined,
          content_css: theme.palette.mode === 'dark' ? 'dark' : undefined,
          height: EDITOR_HEIGHT,
          menubar: false,
          plugins: [
            'advlist',
            'autolink',
            'lists',
            'link',
            'image',
            'charmap',
            'anchor',
            'searchreplace',
            'visualblocks',
            'code',
            'fullscreen',
            'insertdatetime',
            'media',
            'table',
            'preview',
            'help',
            'wordcount',
          ],
          toolbar:
            'undo redo | blocks | ' +
            'bold italic forecolor | link | alignleft aligncenter ' +
            'alignright alignjustify | bullist numlist outdent indent | ' +
            'removeformat | help',
          content_style:
            'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
        }}
        onChange={() => isChanged(true)}
      />
    );
  };

  const renderSkeleton = () => {
    // Mimics the TinyMCE editor footprint (toolbar row + content area) so the
    // layout doesn't shift once the editor renders.
    const toolbarButtonWidths = [64, 80, 56, 48, 48, 72, 40, 40, 96];
    return (
      <Stack spacing={1} aria-busy="true" aria-label="Loading Terms of Service">
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {toolbarButtonWidths.map((width, index) => (
            <Skeleton key={index} variant="rounded" width={width} height={30} />
          ))}
        </Stack>
        <Skeleton variant="rounded" width="100%" height={EDITOR_HEIGHT - 46} />
      </Stack>
    );
  };

  return (
    <CardView
      headerActions={
        <IconButtonMenu
          buttons={[
            {
              label: 'Save',
              startIcon: <SaveIcon />,
              onClick: handleEditorChange,
              disabled: buttonDisabled,
            },
          ]}
        />
      }
      content={
        <Stack px={1.5} pb={2}>
          {loaded ? renderEditor() : renderSkeleton()}
        </Stack>
      }
      title={'Terms of Service'}
    />
  );
};

export default Terms;
